<div class="base-container logbook-main-container">
  <!-- Während des PDF-Exports (exportAsPdf()) verändert sich die Seite
       sichtbar (Theme-Umschaltung, feste Export-Breite, ausgeblendete
       Buttons/Eingabefelder) – dieses vollflächige Overlay deckt das ab
       und macht klar, dass das erwartet ist. -->
  @if (isExportingPdf()) {
    <app-loading-overlay
      [text]="'PRODUCT.OPREPORT.EDIT.PDF_EXPORT_OVERLAY_TEXT' | translate"
      [icon]="['far', 'file-pdf']">
    </app-loading-overlay>
  }

  <!-- Nur im Druck sichtbar (siehe @media print) -->
  <h1 class="print-title">
    {{ 'PRODUCT.OPREPORT.EDIT.PRINT_TITLE' | translate }}
  </h1>

  <!-- 2. LOGBUCH DATEN-TABELLE (Eingebettet in Milchglas-Card) -->
  <div class="flex-flow-wrapper">
    <mat-card appearance="outlined" class="logbook-data-card">
      <div class="operation-form">
        <div class="operation-form__header">
          <span
            class="status-chip"
            [class.status-chip--open]="operationStatus() === 'open'"
            [class.status-chip--closed]="operationStatus() === 'closed'">
            {{
              (operationStatus() === 'open'
                ? 'PRODUCT.OPREPORT.EDIT.STATUS_OPEN'
                : 'PRODUCT.OPREPORT.EDIT.STATUS_CLOSED'
              ) | translate
            }}
          </span>
          <span class="operation-form__updated-at">
            <fa-icon [icon]="['far', 'clock']"></fa-icon>
            {{ operationLastUpdatedAt() | datex: 'DD.MM. HH:mm' }}
          </span>
        </div>

        <div class="operation-form__timestamps">
          <h2 class="operation-form__title">
            {{ 'PRODUCT.OPREPORT.EDIT.OPENED_AT' | translate }}
            {{ operationOpenedAt() | datex: 'DD.MM.YYYY' }}
          </h2>
          @if (
            operationStatus() === 'closed' && operationClosedAt();
            as closedAt
          ) {
            <h2 class="operation-form__title operation-form__title--closed">
              {{ 'PRODUCT.OPREPORT.EDIT.CLOSED_AT_HEADING' | translate }}
              {{ closedAt | datex: 'DD.MM.YYYY HH:mm' }}
            </h2>
          }
        </div>

        <!-- Direkt unter der Überschrift statt ganz unten unter den
             Kommentaren – vorher lag "Speichern" beim Kommentar-Schreiben
             näher an der Maus als "Absenden", was schon mehrfach zum
             versehentlichen Speichern statt Abschicken des Kommentars
             geführt hat. -->
        <div class="operation-form__actions">
          <button
            type="button"
            class="btn-operation-cancel"
            (click)="onCancelOperation()">
            {{
              (isReadonly()
                ? 'PRODUCT.OPREPORT.EDIT.BACK'
                : 'PRODUCT.OPREPORT.EDIT.CANCEL'
              ) | translate
            }}
          </button>
          @if (!isReadonly()) {
            <button
              type="button"
              class="btn-app-save"
              [disabled]="isSaving()"
              (click)="onSaveOperation()">
              {{ 'PRODUCT.OPREPORT.EDIT.SAVE' | translate }}
            </button>
            <button
              type="button"
              class="btn-app-next"
              [disabled]="isSaving()"
              (click)="onCompleteOperation()">
              {{ 'PRODUCT.OPREPORT.EDIT.CLOSE_OPERATION' | translate }}
            </button>
          }

          <!-- Ersetzt den Browser-nativen Druck (Strg+P) als primären Weg
               für "ausdrucken oder als PDF speichern" – siehe
               exportAsPdf() für den Hintergrund (Chrome/Safari paginieren
               die Einsatzpunktprognose-Tabelle beim nativen Drucken nicht
               zuverlässig). margin-left:auto schiebt ihn innerhalb dieser
               Leiste nach rechts; .operation-form__actions wird beim
               PDF-Export/Druck ohnehin komplett ausgeblendet, siehe
               operational-report.edit.component.scss. -->
          <button
            type="button"
            mat-stroked-button
            class="btn-pdf-export"
            [disabled]="isExportingPdf()"
            (click)="exportAsPdf()">
            @if (isExportingPdf()) {
              <fa-icon
                [icon]="['far', 'spinner-third']"
                animation="spin"></fa-icon>
              {{ 'PRODUCT.OPREPORT.EDIT.PDF_EXPORT_RUNNING' | translate }}
            } @else {
              <fa-icon [icon]="['far', 'file-pdf']"></fa-icon>
              {{ 'PRODUCT.OPREPORT.EDIT.PDF_EXPORT' | translate }}
            }
          </button>
        </div>

        <div class="operation-form__section">
          <label class="operation-form__label">
            {{ 'PRODUCT.OPREPORT.EDIT.LEADER_NAME' | translate }}
          </label>
          <input
            type="text"
            class="leader-name-input"
            [formControl]="leaderNameControl"
            [readonly]="isReadonly()"
            [placeholder]="
              'PRODUCT.OPREPORT.EDIT.LEADER_NAME_PLACEHOLDER' | translate
            " />
        </div>

        <div class="operation-form__section">
          <label class="operation-form__label">
            {{ 'PRODUCT.OPREPORT.EDIT.CATEGORIES' | translate }}
          </label>
          <div class="category-chip-row">
            @for (category of operationCategories(); track $index) {
              @let colorSet = categoryColorSet(category);
              <span
                class="category-chip"
                [class.category-chip--removable]="!isReadonly()"
                [style.background]="colorSet.background"
                [style.color]="colorSet.color"
                [style.border-color]="colorSet.border">
                {{ categoryLabel(category.key) | translate }}
                @if (!isReadonly()) {
                  <button
                    type="button"
                    class="category-chip__remove"
                    [attr.aria-label]="
                      'PRODUCT.OPREPORT.EDIT.REMOVE_CATEGORY' | translate
                    "
                    (click)="removeCategory($index)">
                    <fa-icon [icon]="['far', 'times']"></fa-icon>
                  </button>
                }
              </span>
            }
            @if (!isReadonly()) {
              <button
                type="button"
                class="category-chip category-chip--add"
                [attr.aria-label]="
                  'PRODUCT.OPREPORT.EDIT.ADD_CATEGORY' | translate
                "
                [matMenuTriggerFor]="categoryMenu">
                <fa-icon [icon]="['far', 'plus']"></fa-icon>
              </button>
              <mat-menu #categoryMenu="matMenu">
                @for (
                  predefined of availablePredefinedCategories();
                  track predefined.key
                ) {
                  <button
                    mat-menu-item
                    (click)="addPredefinedCategory(predefined)">
                    <span>{{ predefined.translationKey | translate }}</span>
                  </button>
                }
                <button mat-menu-item (click)="openCustomCategoryInput()">
                  <fa-icon [icon]="['far', 'pen']"></fa-icon>
                  <span>
                    {{ 'PRODUCT.OPREPORT.EDIT.CUSTOM_CATEGORY' | translate }}
                  </span>
                </button>
              </mat-menu>
            }
          </div>

          @if (showCustomCategoryInput()) {
            <div class="category-custom-input-row">
              <input
                type="text"
                class="category-custom-input"
                [formControl]="customCategoryControl"
                [placeholder]="
                  'PRODUCT.OPREPORT.EDIT.CUSTOM_CATEGORY_PLACEHOLDER'
                    | translate
                "
                (keydown.enter)="confirmCustomCategory()"
                (keydown.escape)="cancelCustomCategory()" />
              <button
                type="button"
                class="category-custom-input-confirm"
                [attr.aria-label]="'BUTTON.ok' | translate"
                (click)="confirmCustomCategory()">
                <fa-icon [icon]="['far', 'check']"></fa-icon>
              </button>
              <button
                type="button"
                class="category-custom-input-cancel"
                [attr.aria-label]="'PRODUCT.OPREPORT.EDIT.CANCEL' | translate"
                (click)="cancelCustomCategory()">
                <fa-icon [icon]="['far', 'times']"></fa-icon>
              </button>
            </div>
          }
        </div>

        <div class="operation-form__section">
          <div class="operation-form__section-header">
            <label class="operation-form__label">
              {{ 'PRODUCT.OPREPORT.EDIT.SITUATION_REPORT' | translate }}
            </label>
            @if (!isReadonly()) {
              <button
                type="button"
                class="text-block-button"
                [matMenuTriggerFor]="textBlockMenu">
                <fa-icon [icon]="['far', 'plus']"></fa-icon>
                {{ 'PRODUCT.OPREPORT.EDIT.TEXT_BLOCK' | translate }}
              </button>
              <mat-menu #textBlockMenu="matMenu">
                <button mat-menu-item (click)="insertNoOperationNeededText()">
                  <span>
                    {{
                      'PRODUCT.OPREPORT.EDIT.TEXT_BLOCK_NO_OPERATION'
                        | translate
                    }}
                  </span>
                </button>
                <button
                  mat-menu-item
                  [disabled]="!hasEgpText()"
                  (click)="insertEgpText()">
                  <span>
                    {{
                      'PRODUCT.OPREPORT.EDIT.TEXT_BLOCK_COPY_EGP' | translate
                    }}
                  </span>
                </button>
              </mat-menu>
            }
          </div>
          <textarea
            class="situation-report-textarea"
            rows="5"
            [formControl]="situationReportControl"
            [readonly]="isReadonly()"
            [placeholder]="
              'PRODUCT.OPREPORT.EDIT.SITUATION_REPORT_PLACEHOLDER' | translate
            "></textarea>
          <!-- Nur im Druck sichtbar (siehe @media print): das <textarea>
               oben clippt seinen Inhalt im Ausdruck auf die feste
               Zeilenzahl statt mitzuwachsen, dieser reine Text nicht. -->
          <div class="situation-report-print">
            {{ situationReportControl.value }}
          </div>
        </div>

        <!-- Bearbeitungs-Timeline statt reiner Kommentarliste: eröffnet +
             jeder Kommentar + ggf. abgeschlossen, chronologisch – siehe
             timelineEntries(). Dadurch ist dieser Bereich nie leer (anders
             als vorher operationComments), der Titel bleibt also immer
             sichtbar. -->
        <div class="operation-form__comments">
          <label class="operation-form__label">
            <fa-icon [icon]="['far', 'comment-lines']"></fa-icon>
            {{ 'PRODUCT.OPREPORT.EDIT.COMMENTS' | translate }}
          </label>

          <div class="comment-list">
            @for (entry of timelineEntries(); track $index) {
              <div
                class="comment-item"
                [class.comment-item--event]="entry.type !== 'comment'">
                <div class="comment-item__meta">
                  <span class="comment-item__author">
                    @if (entry.type === 'opened') {
                      {{
                        'PRODUCT.OPREPORT.EDIT.TIMELINE_OPENED'
                          | translate
                            : {
                                name:
                                  entry.authorName ||
                                  ('PRODUCT.OPREPORT.EDIT.UNKNOWN_USER'
                                    | translate),
                              }
                      }}
                    } @else if (entry.type === 'closed') {
                      {{
                        'PRODUCT.OPREPORT.EDIT.TIMELINE_CLOSED'
                          | translate
                            : {
                                name:
                                  entry.authorName ||
                                  ('PRODUCT.OPREPORT.EDIT.UNKNOWN_USER'
                                    | translate),
                              }
                      }}
                    } @else {
                      {{
                        entry.authorName ||
                          ('PRODUCT.OPREPORT.EDIT.UNKNOWN_USER' | translate)
                      }}
                    }
                  </span>
                  <span class="comment-item__time">
                    {{ entry.at | datex: 'DD.MM. HH:mm' }}
                  </span>
                </div>
                @if (entry.type === 'comment') {
                  <span class="comment-item__text">{{ entry.text }}</span>
                }
              </div>
            }
          </div>

          @if (!isReadonly()) {
            <div class="comment-input-row">
              <input
                type="text"
                class="comment-input"
                [formControl]="newCommentControl"
                [placeholder]="
                  'PRODUCT.OPREPORT.EDIT.COMMENT_PLACEHOLDER' | translate
                "
                (keydown.enter)="addComment()" />
              <button
                type="button"
                class="comment-input-send"
                [disabled]="!newCommentControl.value?.trim()"
                [attr.aria-label]="
                  'PRODUCT.OPREPORT.EDIT.COMMENT_SEND' | translate
                "
                (click)="addComment()">
                <fa-icon [icon]="['far', 'paper-plane']"></fa-icon>
              </button>
            </div>
          }
        </div>
      </div>
    </mat-card>
    <mat-card appearance="outlined" class="logbook-data-card">
      <div class="weather-now">
        <!-- "Wetter jetzt" passt nur, solange der Einsatz noch angelegt
             wird (Wetter-Bereich zeigt dann tatsächlich den aktuellen
             Moment). Nach dem ersten Speichern ist er auf den
             Einsatzbeginn "eingefroren" (siehe canEditWeatherConfig) – der
             Titel zeigt dann stattdessen Datum + Uhrzeit dieses
             Zeitpunkts. -->
        <h2 class="weather-now__title">
          @if (opReportId() === null) {
            {{ 'PRODUCT.OPREPORT.EDIT.WEATHER_NOW' | translate }}
          } @else {
            {{
              'PRODUCT.OPREPORT.EDIT.WEATHER_AT'
                | translate
                  : {
                      dateTime: operationOpenedAt().format('DD.MM.YYYY HH:mm'),
                    }
            }}
          }
        </h2>

        @if (egpConfig(); as cfg) {
          <div class="weather-now__egp weather-now__card">
            <div class="weather-now__egp-header">
              <div class="weather-now__egp-icon">
                <fa-icon [icon]="['far', 'border-all']"></fa-icon>
              </div>
              <h3 class="weather-now__egp-title">
                {{ 'HOME.DASHBOARD.PRODUCT.EGP' | translate }} {{ cfg.name }}
              </h3>
              @if (egpUpdatedAt(); as dtg) {
                <span class="weather-now__egp-updated-at">
                  <fa-icon [icon]="['far', 'clock']"></fa-icon>
                  {{ dtg | datex: 'DD.MM. HH:mm' }}
                </span>
              }
            </div>

            @for (
              activeCfg of [cfg];
              track activeCfg.region +
                '_' +
                activeCfg.season +
                '_' +
                activeCfg.view
            ) {
              <app-dashboard-egp
                [egpConfig]="activeCfg"
                [refresh]="egpRefresh()"
                [showUpdatedChip]="false"
                [snapshotData]="egpSnapshot()"
                (dtgChange)="onEgpDtgChange($event)"
                (egpResponseChange)="onEgpResponseChange($event)">
              </app-dashboard-egp>
            }

            @if (egpOptions().length > 1 && canEditWeatherConfig()) {
              <button
                type="button"
                mat-icon-button
                class="weather-now__card-settings-button"
                matTooltip="Karte bearbeiten"
                [matMenuTriggerFor]="egpMenu">
                <fa-icon [icon]="['far', 'cog']"></fa-icon>
              </button>
              <mat-menu #egpMenu="matMenu">
                @for (
                  option of egpOptions();
                  track option.region + '_' + option.season
                ) {
                  <button mat-menu-item (click)="selectEgp(option)">
                    @if (
                      cfg.region === option.region &&
                      cfg.season === option.season
                    ) {
                      <fa-icon [icon]="['far', 'check']"></fa-icon>
                    }
                    <span>{{ option.name }}</span>
                  </button>
                }
              </mat-menu>
            }
          </div>
        }

        <div class="weather-now__row">
          @if (egwConfig(); as warnCfg) {
            <div class="weather-now__warning weather-now__card">
              <div class="weather-now__warning-header">
                <div class="weather-now__warning-icon">
                  <fa-icon [icon]="['far', 'exclamation-triangle']"></fa-icon>
                </div>
                <div class="weather-now__warning-header-text">
                  <h3 class="weather-now__warning-title">
                    {{ 'HOME.DASHBOARD.PRODUCT.EGW' | translate }}
                  </h3>
                  <span class="weather-now__warning-subtitle">
                    {{ warnCfg.name }}
                  </span>
                </div>
              </div>

              @for (
                activeCfg of [warnCfg];
                track activeCfg.region + '_' + activeCfg.season
              ) {
                <app-dashboard-warning
                  [egwConfig]="activeCfg"
                  [isTopCard]="false"
                  [refresh]="warnRefresh()"
                  [snapshotWarning]="egwSnapshot()"
                  (displayedWarningChange)="onDisplayedWarningChange($event)">
                </app-dashboard-warning>
              }

              @if (egwOptions().length > 1 && canEditWeatherConfig()) {
                <button
                  type="button"
                  mat-icon-button
                  class="weather-now__card-settings-button"
                  matTooltip="Karte bearbeiten"
                  [matMenuTriggerFor]="warnMenu">
                  <fa-icon [icon]="['far', 'cog']"></fa-icon>
                </button>
                <mat-menu #warnMenu="matMenu">
                  @for (
                    option of egwOptions();
                    track option.region + '_' + option.season
                  ) {
                    <button mat-menu-item (click)="selectWarning(option)">
                      @if (
                        warnCfg.region === option.region &&
                        warnCfg.season === option.season
                      ) {
                        <fa-icon [icon]="['far', 'check']"></fa-icon>
                      }
                      <span>{{ option.name }}</span>
                    </button>
                  }
                </mat-menu>
              }
            </div>
          }

          @if (smwConfig(); as smwCfg) {
            <div class="weather-now__smw weather-now__card">
              <div class="weather-now__smw-header">
                <div class="weather-now__smw-icon">
                  <fa-icon [icon]="['far', 'thermometer-half']"></fa-icon>
                </div>
                <div class="weather-now__smw-header-text">
                  <h3 class="weather-now__smw-title">
                    {{ 'HOME.DASHBOARD.PRODUCT.SMW' | translate }}
                  </h3>
                  <span class="weather-now__smw-subtitle">
                    {{
                      'PRODUCT.SMW.STATIONTYPE.' + smwCfg.stationType
                        | uppercase
                        | translate
                    }}
                  </span>
                </div>
              </div>

              <app-dashboard-smw
                [smwConfig]="smwCfg"
                [refresh]="smwRefresh()"
                [snapshotData]="smwSnapshot()"
                (smwResponseChange)="onSmwResponseChange($event)">
              </app-dashboard-smw>

              @if (smwOptions().length > 1 && canEditWeatherConfig()) {
                <button
                  type="button"
                  mat-icon-button
                  class="weather-now__card-settings-button"
                  matTooltip="Karte bearbeiten"
                  [matMenuTriggerFor]="smwMenu">
                  <fa-icon [icon]="['far', 'cog']"></fa-icon>
                </button>
                <mat-menu #smwMenu="matMenu">
                  @for (option of smwOptions(); track option.stationType) {
                    <button mat-menu-item (click)="selectSmw(option)">
                      @if (smwCfg.stationType === option.stationType) {
                        <fa-icon [icon]="['far', 'check']"></fa-icon>
                      }
                      <span>
                        {{
                          'PRODUCT.SMW.STATIONTYPE.' + option.stationType
                            | uppercase
                            | translate
                        }}
                      </span>
                    </button>
                  }
                </mat-menu>
              }
            </div>
          }
        </div>

        @if (eppConfig(); as eppCfg) {
          <div class="weather-now__epp weather-now__card">
            <div class="weather-now__epp-header">
              <div class="weather-now__epp-icon">
                <fa-icon [icon]="['far', 'clouds-sun']"></fa-icon>
              </div>
              <div class="weather-now__epp-header-text">
                <h3 class="weather-now__epp-title">
                  {{ 'HOME.DASHBOARD.PRODUCT.EPP' | translate }}
                </h3>
                <span class="weather-now__epp-subtitle">
                  {{ eppCfg.station.overwriteStationName }}
                  [{{
                    'PRODUCT.EPP.PARAMETERSET.SETS.' + eppCfg.parameterset
                      | translate
                  }}]
                </span>
              </div>
              @if (eppLastIssueTime(); as dtg) {
                <span class="weather-now__epp-updated-at">
                  <fa-icon [icon]="['far', 'clock']"></fa-icon>
                  {{ dtg | datex: 'DD.MM. HH:mm' }}
                </span>
              }
            </div>

            @for (
              activeCfg of [eppCfg];
              track activeCfg.station.stationId + '_' + activeCfg.parameterset
            ) {
              <app-dashboard-epp
                [eppConfig]="activeCfg"
                [refresh]="eppRefresh()"
                [snapshotForecast]="eppSnapshot()"
                (lastIssueTimeChange)="onEppLastIssueTimeChange($event)"
                (eppResponseChange)="onEppResponseChange($event)">
              </app-dashboard-epp>
            }

            @if (
              (eppOptions().length > 1 || eppCfg.allParametersets.length > 1) &&
              canEditWeatherConfig()
            ) {
              <button
                type="button"
                mat-icon-button
                class="weather-now__card-settings-button"
                matTooltip="Karte bearbeiten"
                [matMenuTriggerFor]="eppMenu">
                <fa-icon [icon]="['far', 'cog']"></fa-icon>
              </button>
              <mat-menu #eppMenu="matMenu">
                @if (eppOptions().length > 1) {
                  <button
                    mat-menu-item
                    disabled
                    class="weather-now__menu-header">
                    {{ 'PRODUCT.OPREPORT.EDIT.EPP_STATION' | translate }}
                  </button>
                  @for (option of eppOptions(); track option.stationId) {
                    <button mat-menu-item (click)="selectEppStation(option)">
                      @if (eppCfg.station.stationId === option.stationId) {
                        <fa-icon [icon]="['far', 'check']"></fa-icon>
                      }
                      <span>{{ option.overwriteStationName }}</span>
                    </button>
                  }
                }
                @if (
                  eppOptions().length > 1 && eppCfg.allParametersets.length > 1
                ) {
                  <mat-divider></mat-divider>
                }
                @if (eppCfg.allParametersets.length > 1) {
                  <button
                    mat-menu-item
                    disabled
                    class="weather-now__menu-header">
                    {{ 'PRODUCT.OPREPORT.EDIT.EPP_PARAMETERSET' | translate }}
                  </button>
                  @for (
                    parameterset of eppCfg.allParametersets;
                    track parameterset
                  ) {
                    <button
                      mat-menu-item
                      (click)="selectEppParameterset(parameterset)">
                      @if (eppCfg.parameterset === parameterset) {
                        <fa-icon [icon]="['far', 'check']"></fa-icon>
                      }
                      <span>
                        {{
                          'PRODUCT.EPP.PARAMETERSET.SETS.' + parameterset
                            | translate
                        }}
                      </span>
                    </button>
                  }
                }
              </mat-menu>
            }
          </div>
        }
      </div>
    </mat-card>
  </div>
</div>
